import {
  AnimatedContainer,
  AnimatedPage,
  AnimatedSection
} from '@components/animated'
import { useApplication } from '@services/applications/queries/useApplication'
import { useProfile } from '@services/users/queries/useProfile'
import { ArrowRight, FileText, User as UserIcon } from 'lucide-react'
import { posthog } from 'posthog-js'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import {
  calculateApplicationCompletion,
  calculateOverallCompletion,
  calculateProfileCompletion
} from './utils/completionCalculator'

function CircularProgress({
  percent,
  size = 96
}: {
  percent: number
  size?: number
}) {
  const stroke = 7
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const offset = circ - (Math.max(0, Math.min(100, percent)) / 100) * circ
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(4,33,37,0.08)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#11adbb"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display text-xl font-bold text-ink">
        {percent}%
      </div>
    </div>
  )
}

function Bar({ percent }: { percent: number }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-ink/[0.06]">
      <div
        className="h-full rounded-full bg-primary transition-all duration-500"
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </div>
  )
}

function StepCard({
  icon: Icon,
  title,
  description,
  action,
  children
}: {
  icon: typeof UserIcon
  title: string
  description: string
  action: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Card className="flex h-full flex-col items-center gap-3 p-5 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="m-0 font-display text-lg font-bold tracking-tight text-ink">
        {title}
      </h3>
      {children}
      <p className="m-0 text-sm leading-relaxed text-muted-foreground">
        {description}
      </p>
      <div className="mt-auto pt-2">{action}</div>
    </Card>
  )
}

export function Welcome() {
  const { t } = useTranslation('welcome')
  const navigate = useNavigate()

  const { data: profile, isLoading: profileLoading } = useProfile(true)
  const { data: application, isLoading: applicationLoading } = useApplication()

  const isLoading = profileLoading || applicationLoading

  const profileCompletion = calculateProfileCompletion(profile)
  const applicationCompletion = calculateApplicationCompletion(application)
  const overallCompletion = calculateOverallCompletion(profile, application)

  const handleNavigateToProfile = () => {
    posthog.capture('Onboarding Navigate to Profile')
    navigate('/user/profile')
  }

  const handleNavigateToApplication = () => {
    posthog.capture('Onboarding Navigate to Application')
    navigate('/user/application')
  }

  const handleSkip = () => {
    posthog.capture('Onboarding Skipped')
    navigate('/user/profile')
  }

  if (isLoading) {
    return (
      <AnimatedPage>
        <div className="flex min-h-[60vh] items-center justify-center">
          <CircularProgress percent={0} />
        </div>
      </AnimatedPage>
    )
  }

  return (
    <AnimatedPage className="flex min-h-0 w-full flex-1 flex-col">
      <div className="flex w-full flex-1 flex-col">
        <AnimatedContainer className="flex flex-1 flex-col">
          {/* Header */}
          <AnimatedSection>
            <div className="flex flex-col items-center text-center">
              <h1 className="m-0 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                {t('title')}
              </h1>
              <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground sm:text-base">
                {t('subtitle')}
              </p>
              <div className="mt-4 flex flex-col items-center gap-1.5">
                <CircularProgress percent={overallCompletion} />
                <span className="text-sm font-medium text-ink/70">
                  {t('overallProgress')}
                </span>
              </div>
            </div>
          </AnimatedSection>

          {/* Step cards */}
          <AnimatedSection>
            <div className="mx-auto mt-6 grid w-full max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
              <StepCard
                icon={UserIcon}
                title={t('steps.profile.title')}
                description={t('steps.profile.description')}
                action={
                  <Button
                    variant={
                      profileCompletion === 100 ? 'ghostDark' : 'default'
                    }
                    onClick={handleNavigateToProfile}
                  >
                    {profileCompletion === 100
                      ? t('steps.profile.buttonComplete')
                      : t('steps.profile.button')}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                }
              >
                <Bar percent={profileCompletion} />
              </StepCard>

              <StepCard
                icon={FileText}
                title={t('steps.application.title')}
                description={t('steps.application.description')}
                action={
                  <Button
                    variant={
                      applicationCompletion === 100 ? 'ghostDark' : 'default'
                    }
                    onClick={handleNavigateToApplication}
                  >
                    {applicationCompletion === 100
                      ? t('steps.application.buttonComplete')
                      : t('steps.application.button')}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                }
              >
                <Bar percent={applicationCompletion} />
              </StepCard>

            </div>
          </AnimatedSection>

          {/* Skip */}
          <AnimatedSection className="mt-auto">
            <div className="flex justify-center pt-10">
              <Button
                variant="link"
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary"
                onClick={handleSkip}
              >
                {t('skipButton')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </AnimatedSection>
        </AnimatedContainer>
      </div>
    </AnimatedPage>
  )
}
